import React, { useEffect, useCallback, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button, Space, Tabs, message, Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  CREATE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE,
  UPDATE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE,
} from '../graphql/mutations';
import { DriverLearningCenterFaqNode } from '../graphql/types';
import { slugify, createWithSlugRetry, generateFallbackSlug } from '../utils/slugify';
import { resolveLearningCenterErrorMessage } from '../utils/errorMessage';

const { TextArea } = Input;

type LangTabKey = 'zh' | 'en' | 'es';

const LANG_TAB_I18N: Record<LangTabKey, string> = {
  zh: 'learningCenterConfig.article.tabZh',
  en: 'learningCenterConfig.article.tabEn',
  es: 'learningCenterConfig.article.tabEs',
};

const langTabOfField = (field: string): LangTabKey | undefined => {
  if (field.endsWith('Zh')) return 'zh';
  if (field.endsWith('En')) return 'en';
  if (field.endsWith('Es')) return 'es';
  return undefined;
};

interface FaqArticleDrawerProps {
  visible: boolean;
  article: DriverLearningCenterFaqNode | null;
  categoryOptions: { value: string; label: string }[];
  defaultParentId: string | null;
  onClose: () => void;
  /** parentId: 保存后所属分类；previousParentId: 编辑前分类（换分类时一并刷新） */
  onSuccess: (parentId: string, previousParentId?: string | null) => void;
}

const FaqArticleDrawer: React.FC<FaqArticleDrawerProps> = ({
  visible,
  article,
  categoryOptions,
  defaultParentId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [activeLangTab, setActiveLangTab] = useState<LangTabKey>('zh');
  const isEdit = !!article;
  // 从某分类「新增问题」或编辑已有问题：所属分类锁定，只能改本分类下内容
  const lockCategory = isEdit || !!defaultParentId;

  const [createArticle, { loading: creating }] = useMutation(CREATE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE);
  const [updateArticle, { loading: updating }] = useMutation(UPDATE_DRIVER_LEARNING_CENTER_FAQ_ARTICLE);
  // 用 Modal.useModal 而非静态 Modal.confirm：静态方法在 React 18 createRoot 下
  // 无法可靠同步内部状态，确定按钮 loading 可能卡死。
  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    if (!visible) return;
    setActiveLangTab('zh');
    if (article) {
      form.setFieldsValue({
        slug: article.slug,
        parentId: article.parentId,
        sortOrder: article.sortOrder,
        questionZh: article.questionZh,
        questionEn: article.questionEn,
        questionEs: article.questionEs,
        answerZh: article.answerZh,
        answerEn: article.answerEn,
        answerEs: article.answerEs,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0, parentId: defaultParentId });
    }
  }, [visible, article, defaultParentId, form]);

  const doSubmit = useCallback(async (values: Record<string, any>) => {
    if (isEdit && article) {
      await updateArticle({
        variables: {
          id: String(article.id),
          input: {
            // 编辑时锁定分类，始终提交原所属分类，避免被表单篡改
            parentId: String(article.parentId),
            questionZh: values.questionZh,
            questionEn: values.questionEn,
            questionEs: values.questionEs,
            answerZh: values.answerZh,
            answerEn: values.answerEn,
            answerEs: values.answerEs,
            sortOrder: values.sortOrder ?? 0,
          },
        },
      });
    } else {
      const parentId = lockCategory && defaultParentId ? defaultParentId : values.parentId;
      const baseSlug = slugify(values.questionEn) || generateFallbackSlug();
      await createWithSlugRetry(baseSlug, (slug) =>
        createArticle({
          variables: {
            input: {
              slug,
              parentId,
              questionZh: values.questionZh,
              questionEn: values.questionEn,
              questionEs: values.questionEs,
              answerZh: values.answerZh,
              answerEn: values.answerEn,
              answerEs: values.answerEs,
              sortOrder: values.sortOrder ?? 0,
            },
          },
        }),
      );
    }
    message.success(t('learningCenterConfig.messages.saveSuccess'));
    const savedParentId = isEdit
      ? String(article?.parentId)
      : String(lockCategory && defaultParentId ? defaultParentId : values.parentId);
    onSuccess(savedParentId, isEdit ? article?.parentId : null);
  }, [isEdit, article, createArticle, updateArticle, onSuccess, t, lockCategory, defaultParentId]);

  const handleSubmit = useCallback(async () => {
    let values: Record<string, any>;
    try {
      values = await form.validateFields();
    } catch (err: any) {
      if (err?.errorFields) {
        const firstField = err.errorFields[0]?.name?.[0];
        const tabKey = typeof firstField === 'string' ? langTabOfField(firstField) : undefined;
        if (tabKey) {
          setActiveLangTab(tabKey);
          const tabLabel = String(t(LANG_TAB_I18N[tabKey]));
          message.warning(t('learningCenterConfig.messages.fillRequiredOnTab', { tab: tabLabel }));
          setTimeout(() => form.scrollToField(firstField), 0);
        }
      }
      return;
    }

    const submitSafely = async () => {
      try {
        await doSubmit(values);
      } catch (err: any) {
        const errorMessage = resolveLearningCenterErrorMessage(err, t);
        if (errorMessage) {
          message.error(errorMessage);
        }
        // 不 rethrow：Modal.onOk 收到 rejected Promise 时 CRA 会弹出运行时红屏
      }
    };

    modal.confirm({
      title: isEdit
        ? t('learningCenterConfig.messages.editArticleConfirmTitle')
        : t('learningCenterConfig.messages.createArticleConfirmTitle'),
      content: isEdit
        ? t('learningCenterConfig.messages.editArticleConfirmContent')
        : t('learningCenterConfig.messages.createArticleConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: submitSafely,
    });
  }, [form, isEdit, doSubmit, modal, t]);

  const requiredRule = [{ required: true, message: String(t('common.required')) }];

  const langTabs = [
    { key: 'zh', label: t('learningCenterConfig.article.tabZh'), questionField: 'questionZh', answerField: 'answerZh' },
    { key: 'en', label: t('learningCenterConfig.article.tabEn'), questionField: 'questionEn', answerField: 'answerEn' },
    { key: 'es', label: t('learningCenterConfig.article.tabEs'), questionField: 'questionEs', answerField: 'answerEs' },
  ];

  return (
    <>
      {contextHolder}
      <Drawer
        title={
          <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
            {isEdit ? t('learningCenterConfig.article.editTitle') : t('learningCenterConfig.article.createTitle')}
          </span>
        }
        width={640}
        visible={visible}
        onClose={onClose}
        closable={false}
        extra={<Button type="text" onClick={onClose} icon={<CloseOutlined />} />}
        footer={
          <Space className="flex justify-end">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="primary" onClick={handleSubmit} loading={creating || updating}>
              {t('common.confirm')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {isEdit && (
            <Form.Item name="slug" label={t('learningCenterConfig.article.slug')}>
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item
            name="parentId"
            label={t('learningCenterConfig.article.category')}
            rules={[{ required: true, message: String(t('learningCenterConfig.messages.selectCategoryFirst')) }]}
          >
            <Select
              options={categoryOptions}
              disabled={lockCategory}
              placeholder={String(t('learningCenterConfig.messages.selectCategoryFirst'))}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('learningCenterConfig.article.sortOrder')}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Tabs
            activeKey={activeLangTab}
            onChange={(key) => setActiveLangTab(key as LangTabKey)}
            items={langTabs.map(({ key, label, questionField, answerField }) => ({
              key,
              label,
              forceRender: true,
              children: (
                <>
                  <Form.Item name={questionField} label={t('learningCenterConfig.article.question')} rules={requiredRule}>
                    <Input maxLength={500} />
                  </Form.Item>
                  <Form.Item name={answerField} label={t('learningCenterConfig.article.answer')} rules={requiredRule}>
                    <TextArea rows={6} maxLength={2000} showCount />
                  </Form.Item>
                </>
              ),
            }))}
          />
        </Form>
      </Drawer>
    </>
  );
};

export default FaqArticleDrawer;
